import { getAppUrl } from "@src/appV2/environment/getAppUrl";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { APP_CUSTOM_SCHEME } from "@src/mobile/utils/deepLinkHandler";
import { Platform } from "@src/mobile/utils/platform";

// Amplify's OAuth listener auto-handles `?code=...&state=...` on page load by
// exchanging the code itself. On Capacitor we own the exchange path
// (`exchangeCodeOnCapacitor`), so the listener has no PKCE state to use — it
// gives up and calls `cognitoUserPoolsTokenProvider.clear()`, which wipes the
// tokens we just stored. Skip the import on Capacitor; web still needs it.
if (!Platform.isMobile) {
  void import("aws-amplify/auth/enable-oauth-listener");
}
import { Amplify } from "aws-amplify";
import { confirmSignIn, signIn, signOut, signUp, updatePassword } from "aws-amplify/auth";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";
import { v4 as uuid } from "uuid";

import { EMAIL_LOGIN_LINK_PATH } from "../AuthRedirect/paths";
import { SSO_CALLBACK_PATH } from "../RipplingSso/paths";
import { fetchAuthSessionWithLock } from "./cognitoSessionLock";
import { cognitoConfig } from "./config";
import { setMagicLinkEmail } from "./magicLinkEmailStore";
import {
  capabilitiesIncludeUsablePassword,
  isSignUpNotAvailableErrorMessage,
  parseCapabilitiesFromSignUpErrorMessage,
  parseCognitoUsernameFromSignUpErrorMessage,
} from "./parseCognitoUsernameFromSignUpErrorMessage";
import { retryOnTransientSerializationError } from "./transientAuthError";

// On Capacitor, the WebView origin (capacitor://localhost) is not registered
// in Cognito as a valid OAuth redirect URI. Use the app's custom URL scheme
// instead so Cognito redirects back to the native app via deep link.
const appOrigin = Platform.isMobile
  ? `${APP_CUSTOM_SCHEME}://`
  : (globalThis.location?.origin ?? "");

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: cognitoConfig.userPoolId,
      userPoolClientId: cognitoConfig.clientId,
      loginWith: {
        oauth: {
          domain: cognitoConfig.oauthDomain,
          scopes: ["email", "openid", "profile"],
          redirectSignIn: [`${appOrigin}${SSO_CALLBACK_PATH}`],
          redirectSignOut: [`${appOrigin}/enter`],
          responseType: "code",
        },
      },
    },
  },
});

const COGNITO_STORAGE_PREFIX = "CognitoIdentityServiceProvider";
cognitoUserPoolsTokenProvider.setKeyValueStorage({
  async getItem(key: string): Promise<string | null> {
    return localStorage.getItem(key);
  },
  async setItem(key: string, value: string): Promise<void> {
    localStorage.setItem(key, value);
  },
  async removeItem(key: string): Promise<void> {
    localStorage.removeItem(key);
  },
  async clear(): Promise<void> {
    const keysToRemove: string[] = [];
    for (let index = 0; index < localStorage.length; index++) {
      const key = localStorage.key(index);
      if (key?.startsWith(COGNITO_STORAGE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    for (const key of keysToRemove) {
      localStorage.removeItem(key);
    }
  },
});

export interface CognitoUserCredential {
  idToken: string;
  accessToken?: string;
}

/**
 * Outcome of the PreSignUp round trip: the Cognito username to initiate auth
 * with, plus the account's decoded sign-in capabilities. `capabilities` is the
 * raw hex bitmap the Lambda embedded (0 when absent/older Lambda), and
 * `hasUsablePassword` is bit 0 — surfaced so callers can prefer the password
 * prompt over email OTP.
 */
export interface EnsureCognitoUserResult {
  cognitoUsername: string;
  capabilities: number;
  hasUsablePassword: boolean;
}

const CHALLENGE_TYPE = {
  MAGIC_LINK: "MAGIC_LINK",
} as const;

type ChallengeType = (typeof CHALLENGE_TYPE)[keyof typeof CHALLENGE_TYPE];

/**
 * Builds the URL the Cognito Lambda uses as the base of the magic link it
 * emails out. Does NOT include the requester's email — sharing the URL
 * would otherwise disclose the email and defeat the cache/prompt gate on
 * the redeem page. Mirrors mobile's `getMagicLinkRedirectUrl` exactly.
 */
function getAppRedirectUrl(): string {
  const appDomain = getAppUrl();
  const requestedUrl = new URLSearchParams(globalThis.location.search).get("requestedUrl");
  if (!requestedUrl) {
    return `${appDomain}${EMAIL_LOGIN_LINK_PATH}`;
  }
  const searchParams = new URLSearchParams({ requestedUrl });
  return `${appDomain}${EMAIL_LOGIN_LINK_PATH}?${searchParams.toString()}`;
}

/**
 * Triggers the PreSignUp Lambda to JIT-create the Cognito user if needed.
 * The signup is always rejected by the Lambda. The expected rejection may include
 * the Cognito username that can be used to initiate auth when the email alias is
 * not active.
 */
export async function ensureCognitoUser(email: string): Promise<EnsureCognitoUserResult> {
  try {
    // Password must satisfy the Cognito user pool policy (uppercase, lowercase)
    // so the request reaches the PreSignUp Lambda.
    const password = `P@0a${uuid()}`;
    await signUp({ username: uuid(), password, options: { userAttributes: { email } } });
    return { cognitoUsername: email, capabilities: 0, hasUsablePassword: false };
  } catch (error: unknown) {
    // The PreSignUp Lambda always rejects admin-app sign-ups, so most errors here
    // are expected. However, if the Lambda specifically threw "USER_NOT_FOUND",
    // it means the user does not exist in the backend — surface this to the caller
    // so we fail fast instead of proceeding to initiateCustomAuth (which would fail
    // with a misleading NotAuthorizedException).
    const errorName =
      error && typeof error === "object" && "name" in error
        ? (error as { name?: unknown }).name
        : undefined;
    const errorMessage =
      error && typeof error === "object" && "message" in error
        ? (error as { message?: unknown }).message
        : undefined;

    if (
      errorName === "UserLambdaValidationException" &&
      typeof errorMessage === "string" &&
      errorMessage.includes("USER_NOT_FOUND")
    ) {
      const userNotFoundError = new Error(
        "No registered user found for the provided email address."
      );
      (userNotFoundError as { name: string }).name = "UserNotFoundException";
      throw userNotFoundError;
    }

    const isExpectedRejection =
      errorName === "UserLambdaValidationException" &&
      typeof errorMessage === "string" &&
      isSignUpNotAvailableErrorMessage({ errorMessage });

    logEvent(
      isExpectedRejection
        ? APP_V2_APP_EVENTS.COGNITO_PRE_SIGN_UP_REJECTED
        : APP_V2_APP_EVENTS.COGNITO_PRE_SIGN_UP_UNEXPECTED_ERROR,
      {
        email,
        errorName: typeof errorName === "string" ? errorName : undefined,
        errorMessage: typeof errorMessage === "string" ? errorMessage : undefined,
      }
    );
    // Use the Cognito username and capabilities when the expected rejection
    // carried them; fall back to email with no capabilities otherwise (also
    // covers unexpected errors, which we ignore).
    if (isExpectedRejection && typeof errorMessage === "string") {
      const capabilities = parseCapabilitiesFromSignUpErrorMessage({ errorMessage });
      return {
        cognitoUsername: parseCognitoUsernameFromSignUpErrorMessage({ errorMessage }) ?? email,
        capabilities,
        hasUsablePassword: capabilitiesIncludeUsablePassword(capabilities),
      };
    }

    return { cognitoUsername: email, capabilities: 0, hasUsablePassword: false };
  }
}

function isUserAlreadyAuthenticatedException(error: unknown): boolean {
  return (
    error !== null &&
    typeof error === "object" &&
    "name" in error &&
    (error as { name: string }).name === "UserAlreadyAuthenticatedException"
  );
}

/**
 * Amplify v6 converts Cognito's PasswordResetRequiredException into a
 * RESET_PASSWORD next step instead of throwing. This helper surfaces it
 * as an error so callers can display an appropriate message.
 *
 * For CUSTOM_AUTH flow, Cognito does NOT throw PasswordResetRequiredException
 * during InitiateAuth — it starts the custom challenge flow regardless of
 * user status. The exception surfaces later during RespondToAuthChallenge,
 * so this check is needed after every confirmSignIn call, not just signIn.
 */
function throwIfPasswordResetRequired(result: { nextStep: { signInStep: string } }): void {
  if (result.nextStep.signInStep === "RESET_PASSWORD") {
    const error = new Error("Password reset required for the user");
    error.name = "PasswordResetRequiredException";
    throw error;
  }
}

function buildCustomAuthSignInInput(username: string) {
  return {
    username,
    options: {
      authFlowType: "CUSTOM_WITHOUT_SRP" as const,
      clientMetadata: {
        "client_metadata:custom_challenge:select": "init",
      },
    },
  };
}

export async function initiateCustomAuth(username: string): Promise<void> {
  let result;
  try {
    result = await retryOnTransientSerializationError(() =>
      signIn(buildCustomAuthSignInInput(username))
    );
  } catch (error: unknown) {
    if (!isUserAlreadyAuthenticatedException(error)) {
      throw error;
    }

    // A previous Cognito session still exists (tokens in localStorage).
    // Clear it and retry so the new sign-in flow can proceed.
    await signOut();
    result = await retryOnTransientSerializationError(() =>
      signIn(buildCustomAuthSignInInput(username))
    );
  }

  throwIfPasswordResetRequired(result);

  if (result.nextStep.signInStep !== "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE") {
    throw new Error(
      `Expected CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE but got ${result.nextStep.signInStep}`
    );
  }
}

async function selectMagicLinkChallenge(options?: {
  skipChallengeCreation?: boolean;
}): Promise<void> {
  const clientMetadata: Record<string, string> = {
    target_custom_challenge: CHALLENGE_TYPE.MAGIC_LINK,
    sub_challenge_step: "select",
    requester_url: getAppRedirectUrl(),
  };

  if (options?.skipChallengeCreation) {
    clientMetadata.skip_challenge_creation = "true";
  }

  const result = await confirmSignIn({
    challengeResponse: CHALLENGE_TYPE.MAGIC_LINK,
    options: {
      clientMetadata,
    },
  });

  throwIfPasswordResetRequired(result);

  if (result.nextStep.signInStep !== "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE") {
    throw new Error(
      `Expected CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE after magic link select but got ${result.nextStep.signInStep}`
    );
  }

  const challengeParams = result.nextStep.additionalInfo ?? {};
  if (challengeParams.status === "error") {
    throw new Error(challengeParams.message ?? "Magic link challenge failed");
  }
}

async function respondToChallenge(
  challengeAnswer: string,
  challengeType: ChallengeType
): Promise<CognitoUserCredential> {
  const confirmResult = await confirmSignIn({
    challengeResponse: challengeAnswer,
    options: {
      clientMetadata: {
        target_custom_challenge: challengeType,
        sub_challenge_step: "answer",
        [`client_metadata:custom_challenge:${challengeType.toLowerCase()}`]: "verify",
      },
    },
  });

  throwIfPasswordResetRequired(confirmResult);

  if (confirmResult.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE") {
    const challengeParams = confirmResult.nextStep.additionalInfo ?? {};
    if (challengeParams.status === "error") {
      throw new Error(challengeParams.message ?? "Challenge failed");
    }
  }

  if (confirmResult.isSignedIn) {
    const session = await fetchAuthSessionWithLock();
    const idToken = session.tokens?.idToken?.toString();

    if (!idToken) {
      throw new Error("Missing Cognito id token after authentication");
    }

    return {
      idToken,
      accessToken: session.tokens?.accessToken?.toString(),
    };
  }

  throw new Error("Authentication not completed");
}

interface RequestMagicLinkParams {
  email: string;
  /**
   * The concrete Cognito username to initiate auth with, already resolved by
   * the caller's PreSignUp probe (`sendLoginEmail`). Defaults to the email for
   * users whose email alias can initiate auth on its own. Passing it here keeps
   * `requestMagicLink` from firing a second, redundant PreSignUp sign-up.
   */
  authUsername?: string;
}

export async function requestMagicLink(params: RequestMagicLinkParams): Promise<void> {
  const { email, authUsername = email } = params;

  await initiateCustomAuth(authUsername);
  await selectMagicLinkChallenge();
  // Persist after the Cognito calls succeed so a stale cache isn't left
  // behind when the request itself failed. Mirrors cbh-mobile-app's
  // amplifyAuth.ts (post-`selectChallengeType(MAGIC_LINK)` write). Caching the
  // resolved username lets the same-device redeem skip the PreSignUp probe.
  setMagicLinkEmail({ email, authUsername });
}

export async function completeMagicLinkSignIn(params: {
  payload: string;
  email: string;
  authUsername?: string;
}): Promise<CognitoUserCredential> {
  const { payload, email } = params;
  // Prefer the username resolved at send time and carried through the
  // same-device cache. On a cache miss (cross-device, or a link issued before
  // this shipped) we can't ask the user for their Cognito username, so we
  // re-derive it with the PreSignUp probe.
  const authUsername = params.authUsername ?? (await ensureCognitoUser(email)).cognitoUsername;

  // Re-initiate the full auth flow before responding with the magic link payload.
  // If a previous Cognito session exists, initiateCustomAuth will clear it automatically.
  await initiateCustomAuth(authUsername);
  await selectMagicLinkChallenge({ skipChallengeCreation: true });

  // Cache cleanup happens in AuthRedirect.tsx after `history.replace`, not
  // here. Clearing synchronously would race the parent route tree's remount
  // during `dispatch(initCognitoSession())` — the freshly-mounted AuthRedirect
  // instance needs the magic-link cache (or the in-progress sessionStorage
  // flag) to recognize the post-redemption state and avoid flashing the
  // "already authenticated" screen.
  return await respondToChallenge(payload, CHALLENGE_TYPE.MAGIC_LINK);
}

export async function cognitoSignOut(): Promise<void> {
  await signOut();
}

/**
 * Checks whether a valid Cognito session exists in localStorage.
 * Returns the email of the currently authenticated user, or undefined if no session exists.
 */
export async function getExistingSessionEmail(): Promise<string | undefined> {
  try {
    const session = await fetchAuthSessionWithLock();
    const email = session.tokens?.idToken?.payload?.email;
    return typeof email === "string" ? email : undefined;
  } catch {
    return undefined;
  }
}

export async function getCognitoIdToken(forceRefresh?: boolean): Promise<string | undefined> {
  const session = await fetchAuthSessionWithLock({ forceRefresh });
  return session.tokens?.idToken?.toString();
}

export function hasStoredCognitoSession(): boolean {
  const key = `${COGNITO_STORAGE_PREFIX}.${cognitoConfig.clientId}.LastAuthUser`;
  return localStorage.getItem(key) !== null;
}

export async function getCognitoGroups(): Promise<string[]> {
  const session = await fetchAuthSessionWithLock();
  const groups = session.tokens?.idToken?.payload?.["cognito:groups"];
  return Array.isArray(groups) ? (groups as string[]) : [];
}

export async function changeCognitoPassword(
  newPassword: string,
  oldPassword: string
): Promise<void> {
  await updatePassword({ oldPassword, newPassword });
}
